import { AmpAiPlatform } from '@internal/backstage-plugin-zf-amp-ai-platform';
import { AnnouncementsPage } from '@backstage-community/plugin-announcements';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { apis } from './apis';
import { ApisPage } from './components/apis/apisPage/ApisPage';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { createApp } from '@backstage/app-defaults';
import DarkIcon from '@material-ui/icons/Brightness2';
import { entityPage } from './components/catalog/EntityPage';
import { FeatureFlags } from '@internal/backstage-plugin-zf-feature-flags';
import { GitlabRepoLocationPickerFieldExtension } from './scaffolder/GitlabRepoLocationPicker';
import { HomePage } from './components/home/HomePage';
import LightIcon from '@material-ui/icons/WbSunny';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { orgPlugin } from '@backstage/plugin-org';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { ReviewStep } from './scaffolder/ReviewStep/ReviewStep';
import { Root } from './components/Root';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { ScoreCards } from './components/scorecards/ScoreCards';
import { SearchPage } from '@backstage/plugin-search';
import { searchPage } from './components/search/SearchPage';
import { Teams } from './components/teams/Teams';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { UnifiedThemeProvider } from '@backstage/theme';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { zfDark } from './theme/zfDark';
import { zfLight } from './theme/zfLight';
import { ZfVulnerabilitiesPage } from '@internal/backstage-plugin-zf-vulnerabilities';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { Navigate, Route } from 'react-router-dom';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={[
          {
            apiRef: microsoftAuthApiRef,
            id: 'microsoft-auth-provider',
            message: 'Sign in using your ZF AD account',
            title: 'Azure AD',
          },
        ]}
      />
    ),
  },
  themes: [
    {
      id: 'zf-light',
      title: 'ZF Light',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={zfLight} children={children} />
      ),
    },
    {
      id: 'zf-dark',
      title: 'ZF Dark',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={zfDark} children={children} />
      ),
    },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/home" element={<HomePage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route path="/teams" element={<Teams />} />
    <Route path="/score-cards" element={<ScoreCards />} />
    <Route path="/feature-flags" element={<FeatureFlags />} />
    <Route path="/amp/*" element={<AmpAiPlatform />} />
    <Route path="/apis/*" element={<ApisPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage
          components={{
            ReviewStepComponent: ReviewStep,
          }}
        />
      }
    >
      <ScaffolderFieldExtensions>
        <GitlabRepoLocationPickerFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    {/* DISABLED BECAUSE WE DON'T WANT USERS TO MANUALLY IMPORT ENTITIES INTO THE CATALOG */}
    {/* <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    /> */}
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/announcements" element={<AnnouncementsPage />} />
    <Route path="/zf-vulnerabilities" element={<ZfVulnerabilitiesPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
